import type { FrameworkArgument } from '../framework/framework.d';
import { isSequenceTupleArg, isTupleArg, readSequenceTupleMemberValue, readTupleMemberValue } from './tupleArg';

const STRING_LIKE_ARG_TYPES = /^(string|str|text|path_name)$/i;
const PYTHON_IDENTIFIER_PATH = /^[A-Za-z_][\w]*(\.[A-Za-z_][\w]*)*$/;

export function isPythonIdentifierPath(value: string): boolean {
  return PYTHON_IDENTIFIER_PATH.test(value);
}

export function escapePythonStringLiteral(value: string): string {
  // Inverse of toJsValue's string decode: escape the backslash first, then quotes and control
  // characters, so a value round-trips losslessly (e.g. a newline becomes `\n`, not a raw newline).
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t');
}

export function isFollowPathAction(actionName: string | undefined): boolean {
  return actionName === 'follow_path_action';
}

export function shouldQuotePythonActionArg(arg: Pick<FrameworkArgument, 'name' | 'type'>, actionName?: string): boolean {
  const type = String(arg?.type || '');
  return STRING_LIKE_ARG_TYPES.test(type) || (isFollowPathAction(String(actionName || '')) && String(arg?.name || '') === 'path');
}

export function formatPythonScalar(value: unknown, forceQuote = false): string {
  if (value === undefined || value === null) return 'None';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'string') {
    if (!forceQuote && isPythonIdentifierPath(value)) return value;
    return `"${escapePythonStringLiteral(value)}"`;
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => formatPythonScalar(item, typeof item === 'string')).join(', ')}]`;
  }
  if (typeof value === 'object') {
    return `{${Object.entries(value)
      .map(([key, item]) => `${formatPythonScalar(key, true)}: ${formatPythonScalar(item, typeof item === 'string')}`)
      .join(', ')}}`;
  }
  return String(value);
}

export function formatPythonActionArgumentValue(arg: FrameworkArgument, actionName?: string, value: unknown = arg.value): string {
  if (isSequenceTupleArg(arg)) {
    if (Array.isArray(value)) {
      const members = arg.members || [];
      const rows = (value as unknown[]).map((row) => {
        const formattedMembers = members.map((member, index) => {
          const memberValue = readSequenceTupleMemberValue(row, index, member.name) ?? member.defaultValue;
          return formatPythonScalar(memberValue, shouldQuotePythonActionArg({ name: member.name, type: member.type }, actionName));
        });
        return `(${formattedMembers.join(', ')})`;
      });
      return `[${rows.join(', ')}]`;
    }
    // A non-array value (e.g. framework metadata with no value, or a preserved raw expression)
    // must still be a valid Sequence literal: emit a preserved expression verbatim, else `[]`
    // (never fall through to scalar `None`).
    const rawValue = typeof value === 'string' ? value.trim() : '';
    return rawValue || '[]';
  }
  if (isTupleArg(arg)) {
    const formattedMembers = (arg.members || []).map((member, index) => {
      const memberValue = readTupleMemberValue(arg, index, member.name) ?? member.defaultValue;
      return formatPythonScalar(memberValue, shouldQuotePythonActionArg({ name: member.name, type: member.type }, actionName));
    });
    return `(${formattedMembers.join(', ')})`;
  }
  return formatPythonScalar(value, shouldQuotePythonActionArg(arg, actionName));
}

export function formatPythonActionArgument(arg: FrameworkArgument, actionName?: string): string {
  return `${arg.name}=${formatPythonActionArgumentValue(arg, actionName)}`;
}
